import { Injectable } from '@angular/core';
import { TileSelectService } from '@app/manage-view/services';
import { CreativesetDataService } from '@app/shared/creativeset';
import { UserSettingsService } from '@app/shared/user-settings/state/user-settings.service';
import { VersionsActions } from '@app/shared/versions/state/versions.actions';
import { VersionsService } from '@app/shared/versions/state/versions.service';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { FiltersService } from '../filters.service';
import * as FiltersActions from './filters.actions';
import { CreativeSize } from '@studio/domain/creativeset';

@Injectable()
export class FiltersEffects {
    loadFiltersFromUserSettings$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FiltersActions.loadFiltersFromUserSettings, FiltersActions.init),
            concatLatestFrom(() =>
                this.userSettingsService.getManageViewFilters(
                    this.creativesetDataService.creativeset.id
                )
            ),
            switchMap(([_, filters]) => {
                return of(
                    FiltersActions.loadFiltersFromUserSettingsSuccess({
                        sizes: filters?.sizes || [],
                        statuses: filters?.statuses || [],
                        groupedSizes: filters?.groupedSizes || []
                    })
                );
            })
        );
    });

    // Dispatch updatedFilters
    loadFiltersFromUserSettingsSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                FiltersActions.loadFiltersFromUserSettingsSuccess,
                // Size
                FiltersActions.addSizesToFilter,
                FiltersActions.removeSizesFromFilter,
                FiltersActions.setSizesFilter,
                FiltersActions.clearAllSizesFilter,
                FiltersActions.showAllSizesFilter,
                // Status
                FiltersActions.addStatusesToFilter,
                FiltersActions.removeStatusesFromFilter,
                FiltersActions.setStatusesFilter,
                FiltersActions.clearAllStatusesFilter
            ),
            switchMap(() => of(FiltersActions.updatedFilters()))
        );
    });

    // Save the new state
    updatedFilters$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(FiltersActions.updatedFilters),
                concatLatestFrom(() => this.filtersService.filtersState$),
                tap(([_, { sizes, statuses, groupedSizes }]) => {
                    const creativesetId = this.creativesetDataService.creativeset.id;
                    this.userSettingsService.setManageViewFilters(creativesetId, {
                        sizes,
                        statuses,
                        groupedSizes
                    });
                })
            );
        },
        { dispatch: false }
    );

    initializeVersionsSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(VersionsActions.initSuccess),
            switchMap(({ versions, selectedVersionsIds, defaultVersion }) => {
                if (selectedVersionsIds.includes('all')) {
                    return of(FiltersActions.selectAllVersions());
                }

                const selectableVersionsIds = selectedVersionsIds.filter(id =>
                    versions.some(version => version.id === id)
                );

                let versionIds = [defaultVersion.id];
                if (selectableVersionsIds.length) {
                    versionIds = Array.from(new Set(selectableVersionsIds));
                }
                return of(
                    FiltersActions.setVersionsFilter({
                        versionIds
                    })
                );
            })
        );
    });

    selectAllVersions$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FiltersActions.selectAllVersions),
            concatLatestFrom(() => this.versionsService.selectableVersions$),
            switchMap(([_, selectableVersions]) => {
                return of(
                    FiltersActions.setVersionsFilter({
                        versionIds: selectableVersions.map(({ id }) => id)
                    })
                );
            })
        );
    });

    saveNewVersion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(VersionsActions.createVersionSuccess),
            switchMap(({ versions }) => {
                return of(
                    FiltersActions.setVersionsFilter({
                        versionIds: [versions[0].id]
                    })
                );
            })
        );
    });

    deleteVersions$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(VersionsActions.deleteVersionsSuccess),
            concatLatestFrom(() => [
                this.versionsService.defaultVersion$,
                this.filtersService.selectedVersionIds$
            ]),
            switchMap(([{ versionsIds }, defaultVersion, selectedVersionIds]) => {
                const newSelectedIds = selectedVersionIds.filter(id => !versionsIds.includes(id));
                const newVersionIds = newSelectedIds.length ? newSelectedIds : [defaultVersion.id];
                return of(
                    FiltersActions.setVersionsFilter({
                        versionIds: newVersionIds
                    })
                );
            })
        );
    });

    // Context menu actions
    hideOthers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FiltersActions.hideOtherSizes),
            concatLatestFrom(() => this.tileSelectService.selection$),
            switchMap(([_, selectedCreatives]) => {
                const selectedSizes = selectedCreatives.map(({ size }) => size.id);
                return of(FiltersActions.setSizesFilter({ sizeIds: selectedSizes }));
            })
        );
    });

    hideSelectedSizes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FiltersActions.hideSelectedSizes),
            concatLatestFrom(() => this.tileSelectService.selection$),
            concatLatestFrom(() => this.filtersService.allSizes$),
            switchMap(([[_, selectedCreatives], allSizeIds]) => {
                const selectedSizes = selectedCreatives.map(({ size }) => size.id);
                const newFilteredSizeIds = allSizeIds.filter(sizeId => !selectedSizes.includes(sizeId));
                return of(FiltersActions.setSizesFilter({ sizeIds: newFilteredSizeIds }));
            })
        );
    });

    // Sizes
    toggleAllSizes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FiltersActions.toggleAllSizesFilter),
            concatLatestFrom(() => this.filtersService.isFilteringAnySize$),
            switchMap(([_, isFilteringAnySize]) =>
                isFilteringAnySize
                    ? of(FiltersActions.clearAllSizesFilter())
                    : of(FiltersActions.showAllSizesFilter())
            )
        );
    });

    updateGroupedSizes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FiltersActions.updateGroupedSizes),
            switchMap(() => {
                return of(
                    FiltersActions.updateGroupedSizesSuccess({
                        groupedSizes: this.getGroupedSizes()
                    })
                );
            })
        );
    });

    constructor(
        private actions$: Actions,
        private userSettingsService: UserSettingsService,
        private filtersService: FiltersService,
        private tileSelectService: TileSelectService,
        private creativesetDataService: CreativesetDataService, // TODO: Replace dependency with NgRX,
        private versionsService: VersionsService
    ) {}

    private getGroupedSizes(): IGroupedSize[] {
        const creatives = this.creativesetDataService.creativeset?.creatives || [];
        const grouped: IGroupedSize[] = [];
        for (const creative of creatives) {
            const group = grouped.find(g => g.size.id === creative.size.id);
            if (!group) {
                grouped.push({
                    id: creative.size.id,
                    size: creative.size,
                    selected: false
                });
            }
        }
        return grouped;
    }
}

export interface IGroupedSize {
    size: CreativeSize;
    id: string;
    selected: boolean;
    isHovered?: boolean;
}
